import React, { useState } from 'react';
import { useCartStore } from '../store/useCartStore';
import { Trash2, Minus, Plus } from 'lucide-react';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { shopTranslations } from '../i18n/translations';

const CheckoutPage = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, shopTranslations);
  const cart = useCartStore((state) => state.cartItems);
  const removeItem = useCartStore((state) => state.removeItem);
  const increment = useCartStore((state) => state.increment);
  const decrement = useCartStore((state) => state.decrement);
  const clearCart = useCartStore((state) => state.clearCart);

  const [promoCode, setPromoCode] = useState('');
  const [promo, setPromo] = useState(null); 
  // promo = { type: 'percent'|'fixed', value: number }
  
  const [promoError, setPromoError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const promoCodes = {
    MENDYX5: { type: 'fixed', value: 5 }, // ✅ -5€
  };
  
  const applyPromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
  
    try {
      setPromoError("");
  
      // ✅ 1) Ton code local (ex: MENDYX5)
      const local = promoCodes[code];
      if (local) {
        setPromo(local);
        return;
      }
  
      // ✅ 2) Vérif DB/serveur
      const res = await fetch(
        "https://us-central1-mendyx-adafe.cloudfunctions.net/validatePromoCode",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        }
      );
  
      const data = await res.json();
  
      if (!data.ok) {
        setPromo(null);
        setPromoError(data.error || t('genericError'));
        return;
      }
  
      setPromo(data.promo); // ✅ {type:'fixed',value:25} ou {type:'percent',value:15}
      setPromoError("");
    } catch (e) {
      console.error(e);
      setPromo(null);
      setPromoError(t('genericError'));
    }
  };
  
  

  const totalPrice = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  let discountedTotal = totalPrice;

  if (promo) {
    if (promo.type === 'percent') {
      discountedTotal = totalPrice - (totalPrice * promo.value / 100);
    }
    if (promo.type === 'fixed') {
      discountedTotal = totalPrice - promo.value;
    }
  }
  
  discountedTotal = Math.max(0, discountedTotal); // évite total négatif
  
  const deliveryFee = 8;
const totalWithDelivery = discountedTotal + deliveryFee;


  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleCheckout = async () => {
    setLoading(true);
    setError('');

    if (!firstName || !lastName || !email || !phone || !address) {
      setError("Veuillez remplir tous les champs obligatoires.");
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Veuillez entrer un email valide.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://us-central1-mendyx-adafe.cloudfunctions.net/createCheckoutShopSession', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: {
            firstName,
            lastName,
            email,
            phone,
            address,
            shippingMethod,
            notes,
            promoCode: promoCode.trim().toUpperCase(),
          },
          cartItems: cart,
          language,
        
          // ✅ SEULEMENT les promos FIXES en €
          discount: promo && promo.type === 'fixed' ? promo.value : 0
        })
        
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(t('genericError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-12 px-4">
      <h1 className="text-3xl font-bold text-[#4c2f8e] mb-8 text-center">{t('yourCart')}</h1>

      {cart.length === 0 ? (
        <p className="text-center text-gray-500">{t('emptyCart')}</p>
      ) : (
        <>
          <div className="space-y-6">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center gap-4 border-b pb-4">
                <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded shadow-sm" />
                <div className="flex-1">
                <p className="font-semibold text-[#4c2f8e]">{item.name}</p>
{item.selectedSize && (
  <p className="text-sm text-gray-500">{t('size')} : {item.selectedSize}</p>
)}

                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                    <button onClick={() => decrement(item.id)}><Minus size={16} /></button>
                    <span>{item.quantity}</span>
                    <button onClick={() => increment(item.id)}><Plus size={16} /></button>
                    <span className="ml-3">{item.price} €</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{item.price * item.quantity} €</p>
                  <button onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">{t('promoCode')}</h3>
            <div className="flex gap-3">
              <input type="text" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="border rounded-md px-3 py-2 w-full" placeholder="Ex : " />
              <button onClick={applyPromo} className="bg-[#4c2f8e] text-white px-4 py-2 rounded-md hover:bg-[#351f6a] transition">{t('apply')}</button>
            </div>
            {promoError && <p className="text-sm text-red-500 mt-1">{promoError}</p>}
            {promo && (
  <p className="text-sm text-green-600 mt-1">
    ✅ {t('codeApplied')} : <strong>{promoCode.toUpperCase()}</strong>{' '}
    {promo.type === 'percent' ? `(-${promo.value}%)` : `(-${promo.value}€)`}
  </p>
)}

          </div>

          <div className="mt-10">
            <h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">{t('customerInfo')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="border p-2 rounded" placeholder={t('firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <input className="border p-2 rounded" placeholder={t('lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              <input className="border p-2 rounded col-span-2" placeholder={t('email')} value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="border p-2 rounded col-span-2" placeholder={t('phone')} value={phone} onChange={(e) => setPhone(e.target.value)} />
              <input className="border p-2 rounded col-span-2" placeholder={t('address')} value={address} onChange={(e) => setAddress(e.target.value)} />
             
              <textarea className="border p-2 rounded col-span-2" rows="3" placeholder={t('notes')} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="mt-10 border-t pt-6">
          <div className="mt-6 mb-6 space-y-1 text-[#4c2f8e]">
  <div className="flex justify-between text-base font-medium">
    <span>{t('productsTotal')} :</span>
    <span>
    {promo ? (
  <>
    <span className="line-through text-gray-400 mr-2">{totalPrice.toFixed(2)} €</span>
    <span>{discountedTotal.toFixed(2)} €</span>
  </>
) : (
  <span>{totalPrice.toFixed(2)} €</span>
)}

    </span>
  </div>

  <div className="flex justify-between text-sm">
    <span>{t('shipping')} :</span>
    <span>8,00 €</span>
  </div>

  <hr className="my-2 border-[#e0d7f8]" />

  <div className="flex justify-between text-xl font-bold">
    <span>{t('totalTax')} :</span>
    <span>{totalWithDelivery.toFixed(2)} €</span>
  </div>
</div>

<div className="flex flex-wrap gap-4 items-center">
  <button
    onClick={clearCart}
    className="px-4 py-2 border border-red-400 text-red-500 rounded-full hover:bg-red-100 transition"
  >
    {t('clearCart')}
  </button>
  <button
    onClick={handleCheckout}
    disabled={loading}
    className="px-4 py-2 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full hover:shadow-lg transition"
  >
    {loading ? t('redirecting') : t('payment')}
  </button>
</div>


<p className="text-xs text-gray-500 mt-3 text-center md:text-left">
  {t('validating')}{' '}
  <a
    href="/terms-of-sale"
    target="_blank"
    rel="noopener noreferrer"
    className="text-[#4c2f8e] underline hover:text-[#ea4c89] transition"
  >
    {t('terms')}
  </a>.
</p>

{error && <p className="text-sm text-red-500 mt-2">{error}</p>}

          </div>
        </>
      )}
    </div>
  );
};

export default CheckoutPage;
